import type { CurationQueueItem } from '@ralph/contracts'
import clsx from 'clsx'
import { Quote } from 'lucide-react'
import { useId, useState } from 'react'
import { riseDelay, SectionHead, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Button, EmptyState, Input, Textarea } from '@/components/ui/primitives.tsx'
import { useClearCapabilityCuration, useCurationQueue, useSaveCapabilityCuration } from '@/hooks/use-engagement.ts'
import { describeApiError, useI18n, useT } from '@/i18n/index.ts'
import { DESCRIPTION_MAX, NAME_MAX } from './capability-limits.ts'
import { riskClassLabel } from './system-labels.ts'

/**
 * The curation backlog (AGM-080).
 *
 * One capability per card, each with the two things an administrator needs side by side: what
 * the connected system says about itself, and the field where the platform's own words go.
 * They are visually separated and labeled as such, because the failure mode here is not
 * laziness — it is a plausible-sounding claim being adopted as the platform's description and
 * then read by a maker as if the platform had verified it.
 *
 * The claim is therefore drawn as a quotation — indented behind a rule, with a quote mark and
 * an attribution — and the platform's fields are drawn as fields. Two panels of identical
 * text boxes would let the eye treat the claim as a default value to accept.
 *
 * The read/act choice is the consequential one: it decides which first-use notice a person
 * sees before their first conversation with any agent holding this capability, and a
 * mis-marked "acts" capability is an action taken with someone's authority without the
 * warning they were owed.
 */
export const CapabilityCurationPanel = ({ enabled }: { enabled: boolean }) => {
  const t = useT().admin.capabilities
  const queue = useCurationQueue(enabled)
  const items = queue.data?.items ?? []

  return (
    <section>
      <SectionHead title={t.title} lead={t.lead} />

      {queue.isLoading && <p className="mt-3 text-[13px] text-fg-subtle">{t.loading}</p>}

      {queue.isError && (
        <div className="mt-3">
          <EmptyState title={t.unavailable} body={t.unavailableBody} />
        </div>
      )}

      {!queue.isLoading && !queue.isError && items.length === 0 && (
        <div className="mt-3">
          <EmptyState title={t.empty} body={t.emptyBody} />
        </div>
      )}

      <ul className="mt-4 space-y-2.5">
        {items.map((item, index) => (
          <li key={item.capabilityId}>
            <CurationCard item={item} index={index} />
          </li>
        ))}
      </ul>
    </section>
  )
}

const CurationCard = ({ item, index }: { item: CurationQueueItem; index: number }) => {
  // Two packs, read in the body: this surface's own words, and the connected-systems words
  // for the risk class — which is the same idea worded once for both planes rather than
  // twice (PRD-27 F-3 for the placement, D-9 for the guarded lookup).
  const fmt = useI18n()
  const t = fmt.t.admin.capabilities
  const systems = fmt.t.admin.systems
  const fieldId = useId()
  const save = useSaveCapabilityCuration()
  const clear = useClearCapabilityCuration()

  const [name, setName] = useState(item.curated?.name ?? '')
  const [description, setDescription] = useState(item.curated?.description ?? '')
  const [wording, setWording] = useState<'reads' | 'acts'>(item.curated?.wording ?? 'reads')

  const complete = name.trim() !== '' && description.trim() !== ''

  return (
    <StudioRow style={riseDelay(index)}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        <h3 className="text-[14px]">
          {item.serverName}
          <span className="readout ml-1.5 text-fg-subtle">{item.remoteName}</span>
        </h3>
        <Badge tone={item.curated ? 'grounded' : 'primary'}>{item.curated ? t.curatedBadge : t.uncuratedBadge}</Badge>
        {/* The class in the words the connected-systems plane already uses for it, rather
            than the wire identifier — this rendered "risk external_communication". The queue
            types `risk` as a bare string, so the lookup is the guarded one. */}
        <span className="readout text-fg-subtle">
          {t.riskLabel} {riskClassLabel(systems, item.risk)}
        </span>
      </div>

      {/* Quoted, indented and attributed — the visual grammar of someone else's words. The
          fill is a wash of the ink colour rather than of a surface token, so it recesses in
          the light theme and lifts in the dark one instead of vanishing in whichever is
          nearer to the panel it sits on. */}
      <blockquote className="mt-3 rounded-r-tile border-l-2 border-rule-strong bg-fg/[0.03] py-2.5 pr-3 pl-3.5">
        <p className="eyebrow flex items-center gap-1.5">
          <Quote size={10} strokeWidth={2.6} aria-hidden />
          {t.claimedTitle}
        </p>
        <p className="mt-1.5 text-[13px] leading-snug">{item.claimedTitle || '—'}</p>
        <p className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">{item.claimedDescription || '—'}</p>
        <p className="mt-1.5 text-[12px] leading-snug text-fg-subtle">{t.claimedHint}</p>
        <Button
          size="sm"
          className="mt-2"
          onClick={() => {
            setName(item.claimedTitle.slice(0, NAME_MAX))
            setDescription(item.claimedDescription.slice(0, DESCRIPTION_MAX))
          }}
        >
          {t.useAsDraft}
        </Button>
      </blockquote>

      <div className="mt-3.5 space-y-3.5">
        <div>
          <label htmlFor={`${fieldId}-name`} className="eyebrow mb-1 block">
            {t.nameLabel}
          </label>
          <Input
            id={`${fieldId}-name`}
            value={name}
            maxLength={NAME_MAX}
            placeholder={t.namePlaceholder}
            onChange={(event) => setName(event.target.value)}
          />
          <p className="readout mt-1 text-right text-fg-subtle">{t.nameCount(fmt, NAME_MAX - name.length)}</p>
        </div>

        <div>
          <label htmlFor={`${fieldId}-description`} className="eyebrow mb-1 block">
            {t.descriptionLabel}
          </label>
          <Textarea
            id={`${fieldId}-description`}
            rows={2}
            value={description}
            maxLength={DESCRIPTION_MAX}
            placeholder={t.descriptionPlaceholder}
            onChange={(event) => setDescription(event.target.value)}
          />
          <p className="readout mt-1 text-right text-fg-subtle">
            {t.descriptionCount(fmt, DESCRIPTION_MAX - description.length)}
          </p>
        </div>

        <fieldset>
          <legend className="eyebrow mb-1.5">{t.wordingLabel}</legend>
          <div className="grid gap-1.5 sm:grid-cols-2">
            {(['reads', 'acts'] as const).map((value) => (
              <label
                key={value}
                className={clsx(
                  'flex cursor-pointer items-start gap-2 rounded-tile border border-rule bg-fg/[0.02]',
                  'px-3 py-2 text-[13px] leading-snug transition-colors duration-200 hover:border-rule-strong'
                )}
              >
                <input
                  type="radio"
                  name={`${fieldId}-wording`}
                  value={value}
                  checked={wording === value}
                  onChange={() => setWording(value)}
                  className="mt-[3px] h-3.5 w-3.5 shrink-0 accent-primary"
                />
                <span>{value === 'reads' ? t.wordingReads : t.wordingActs}</span>
              </label>
            ))}
          </div>
          <p className="mt-1.5 text-[12px] leading-snug text-fg-subtle">{t.wordingHint}</p>
        </fieldset>
      </div>

      {save.isError && (
        <p role="alert" className="mt-2.5 text-[12.5px] text-alert">
          {t.saveFailed} {describeApiError(fmt.t, save.error)}
        </p>
      )}
      {clear.isError && (
        <p role="alert" className="mt-2.5 text-[12.5px] text-alert">
          {t.withdrawFailed} {describeApiError(fmt.t, clear.error)}
        </p>
      )}
      {save.isSuccess && !save.isPending && (
        <p role="status" className="mt-2.5 text-[12.5px] text-grounded">
          {t.saved}
        </p>
      )}

      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          disabled={!complete || save.isPending}
          onClick={() =>
            save.mutate({
              capabilityId: item.capabilityId,
              curation: { name: name.trim(), description: description.trim(), wording }
            })
          }
        >
          {save.isPending ? t.saving : t.save}
        </Button>

        {item.curated && (
          <>
            <Button size="sm" disabled={clear.isPending} onClick={() => clear.mutate(item.capabilityId)}>
              {clear.isPending ? t.withdrawing : t.withdraw}
            </Button>
            <span className="text-[12px] text-fg-subtle">{t.withdrawHint}</span>
          </>
        )}
      </div>
    </StudioRow>
  )
}
